<template>
  <div class="edit-person-profile">
    <b-container v-if="!loading && !loadingError">
      <b-row>
        <b-col cols="12" md="8">
          <!-- Login Details Card -->
          <b-card v-if="json_data" class="mb-4">
            <h3 class="card-title">Login Details</h3>
            <form @submit.prevent="editAccount">
              <b-form-group
                label="Username (cannot be changed)"
                label-for="userName"
              >
                <b-form-input
                  id="userName"
                  v-model="json_data.userName"
                  readonly
                  required
                />
              </b-form-group>

              <b-form-group
                label="New Password"
                label-for="password"
                :description="
                  !passwordError
                    ? 'Password must be between 6 and 10 characters.'
                    : ''
                "
              >
                <b-form-input
                  id="password"
                  v-model="json_data.password"
                  type="password"
                  placeholder="Leave blank to keep current password"
                  :state="passwordError ? false : null"
                />
                <b-form-invalid-feedback v-if="passwordError">
                  {{ passwordError }}
                </b-form-invalid-feedback>
              </b-form-group>

              <b-form-group
                label="Repeat New Password"
                label-for="password_confirm"
              >
                <b-form-input
                  id="password_confirm"
                  v-model="json_data.password_confirm"
                  type="password"
                  placeholder="Must be same as above"
                  :state="confirmPasswordError ? false : null"
                />
                <b-form-invalid-feedback v-if="confirmPasswordError">
                  {{ confirmPasswordError }}
                </b-form-invalid-feedback>
              </b-form-group>

              <b-alert v-if="accountFormErrors.length" variant="danger" show>
                <strong>Please fix the following errors:</strong>
                <ul class="mb-0 mt-2">
                  <li v-for="error in accountFormErrors" :key="error">
                    {{ error }}
                  </li>
                </ul>
              </b-alert>

              <b-alert v-if="successMessage2" variant="success" show>
                <i class="bi bi-check-circle-fill me-2" aria-hidden="true"></i>
                {{ successMessage2 }}
              </b-alert>

              <b-button
                type="submit"
                variant="primary"
                :disabled="isLoading || !canUpdateLoginDetails"
              >
                <b-spinner small v-if="isLoading" class="me-2"></b-spinner>
                <span v-if="isLoading">Updating Login Details...</span>
                <span v-else>Update Login Details</span>
              </b-button>
            </form>
          </b-card>

          <!-- User Profile Card -->
          <b-card v-if="json_data" class="mb-4">
            <h3 class="card-title">Profile Information</h3>
            <form @submit.prevent="updatePersonDetails">
              <b-form-group label="First Name" label-for="firstName">
                <b-form-input
                  id="firstName"
                  v-model="json_data.firstName"
                  autocomplete="given-name"
                  required
                />
              </b-form-group>

              <b-form-group label="Last Name" label-for="lastName">
                <b-form-input
                  id="lastName"
                  v-model="json_data.lastName"
                  autocomplete="family-name"
                  required
                />
              </b-form-group>

              <b-form-group label="Email Address" label-for="email">
                <b-form-input
                  id="email"
                  v-model="json_data.email"
                  type="email"
                  autocomplete="email"
                  required
                />
              </b-form-group>

              <b-form-group
                label="Institution Name"
                label-for="institutionName"
              >
                <b-form-input
                  id="institutionName"
                  v-model="json_data.institutionName"
                  required
                />
              </b-form-group>

              <b-form-group label="Country/Region" label-for="country">
                <b-form-select
                  id="country"
                  v-model="json_data.country"
                  :options="countryOptions"
                  @change="onCountryChange"
                  autocomplete="country-name"
                />
              </b-form-group>

              <b-form-group label="State/Province" label-for="stateProvince">
                <b-form-select
                  id="stateProvince"
                  v-model="json_data.region"
                  :options="stateProvinceOptions"
                  autocomplete="address-level1"
                />
              </b-form-group>

              <b-form-checkbox v-model="displayEmail" class="mb-3">
                Display email address on Person detail page
              </b-form-checkbox>

              <b-form-checkbox v-model="sendEmail" class="mb-3">
                Get occasional TAIR newsletters and updates
              </b-form-checkbox>

              <b-alert v-if="updateFormErrors.length" variant="danger" show>
                <strong>Please fix the following errors:</strong>
                <ul class="mb-0 mt-2">
                  <li v-for="error in updateFormErrors" :key="error">
                    {{ error }}
                  </li>
                </ul>
              </b-alert>

              <b-alert v-if="successMessage" variant="success" show>
                <i class="bi bi-check-circle-fill me-2" aria-hidden="true"></i>
                {{ successMessage }}
              </b-alert>

              <b-button type="submit" variant="primary" :disabled="isLoading">
                <b-spinner small v-if="isLoading" class="me-2"></b-spinner>
                <span v-if="isLoading">Updating Profile Data...</span>
                <span v-else>Update Profile Data</span>
              </b-button>
            </form>
          </b-card>
        </b-col>

        <!-- Sidebar Column -->
        <b-col cols="12" md="4">
          <!-- ORCID Information Card -->
          <b-card class="mb-4">
            <h3 class="card-title">ORCID Information</h3>
            <div v-if="orcidFullId">
              <p class="mb-2">ORCID ID:</p>
              <p class="mb-3">
                <strong>{{ orcidFullId }}</strong>
              </p>
              <b-button
                v-if="isTairCurator"
                variant="outline-danger"
                size="sm"
                @click="handleUnlinkOrcid"
                :disabled="isUnlinking"
              >
                <b-spinner small v-if="isUnlinking" class="me-1"></b-spinner>
                <span v-if="isUnlinking">Unlinking...</span>
                <span v-else>Unlink ORCID</span>
              </b-button>
            </div>
            <div v-else>
              <p class="mb-3">No ORCID ID linked to this account.</p>
              <b-button
                v-if="!isViewingOtherProfile"
                variant="success"
                size="sm"
                :href="`/linktoorcid?communityId=${
                  json_data ? json_data.communityId : ''
                }`"
              >
                Link ORCID Account
              </b-button>
            </div>
          </b-card>

          <!-- Account Deactivation Card (Curator Only) -->
          <b-card v-if="isTairCurator && json_data" class="mb-4 deactivate-card">
            <h3 class="card-title text-danger">Account Management</h3>
            <p class="text-muted mb-3">
              As a TAIR curator, you can deactivate this user's account. This
              action will prevent the user from logging in and block account
              recovery.
            </p>

            <b-alert v-if="deactivateError" variant="danger" show dismissible>
              {{ deactivateError }}
            </b-alert>

            <b-alert v-if="deactivateSuccess" variant="success" show>
              {{ deactivateSuccess }}
            </b-alert>

            <b-button
              variant="danger"
              size="sm"
              @click="showDeactivateModal = true"
              :disabled="isDeactivating"
            >
              <i class="bi bi-x-circle me-1" aria-hidden="true"></i>
              Deactivate Account
            </b-button>

            <!-- Deactivation Confirmation Modal -->
            <b-modal
              v-model="showDeactivateModal"
              title="Confirm Account Deactivation"
              header-bg-variant="danger"
              header-text-variant="light"
              centered
              @ok="handleDeactivateAccount"
              ok-title="Yes, Deactivate"
              ok-variant="danger"
              cancel-title="Cancel"
              :ok-disabled="isDeactivating"
            >
              <div v-if="!isDeactivating">
                <p>
                  <strong>Warning:</strong> You are about to deactivate the
                  account for:
                </p>
                <ul>
                  <li>
                    <strong>Name:</strong> {{ json_data.firstName }}
                    {{ json_data.lastName }}
                  </li>
                  <li><strong>Username:</strong> {{ json_data.userName }}</li>
                  <li><strong>Email:</strong> {{ json_data.email }}</li>
                  <li>
                    <strong>Community ID:</strong> {{ json_data.communityId }}
                  </li>
                </ul>
                <p class="text-danger mb-0">
                  <strong>This action will:</strong>
                </p>
                <ul class="text-danger">
                  <li>Set the account status to inactive</li>
                  <li>Prevent the user from logging in</li>
                  <li>Block password and username recovery</li>
                </ul>
                <p>Are you sure you want to proceed?</p>
              </div>
              <div v-else class="text-center py-3">
                <b-spinner label="Deactivating..."></b-spinner>
                <p class="mt-2 mb-0">Deactivating account...</p>
              </div>
            </b-modal>
          </b-card>
        </b-col>
      </b-row>
    </b-container>

    <!-- Loading State -->
    <b-container v-if="loading">
      <div
        class="d-flex justify-content-center align-items-center"
        style="min-height: 300px"
      >
        <b-spinner label="Loading person details..."></b-spinner>
      </div>
    </b-container>

    <!-- Error State -->
    <b-container v-if="loadingError">
      <b-alert variant="danger" show>
        Error fetching data. Please try again later.
      </b-alert>
    </b-container>
  </div>
</template>

<script>
//http://localhost:8082/edit/person?key=1501568961
import Api from "@/services/Api";
import { mapGetters } from "vuex";
import { unlinkOrcid } from "@/services/orcidService";
import { Country, State } from "country-state-city";

export default {
  name: "PersonEditDetail",
  components: {},
  data() {
    return {
      json_data: null,
      formErrors: [],
      loading: false,
      loadingError: false,
      entries: [],
      successMessage: "",
      successMessage2: "",
      isLoading: false,
      accountFormErrors: [],
      updateFormErrors: [],
      passwordError: "",
      confirmPasswordError: "",
      sendEmail: false,
      displayEmail: false,
      originalEmail: null,
      devBypass: false,
      orcidFullId: "",
      isUnlinking: false,
      countries: [],
      statesProvinces: [],
      // Deactivation state (PWL-983)
      showDeactivateModal: false,
      isDeactivating: false,
      deactivateError: "",
      deactivateSuccess: "",
    };
  },

  async mounted() {
    document.title = "Edit Person Details";

    // Add Bootstrap Icons
    const link = document.createElement("link");
    link.href =
      "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/font/bootstrap-icons.css";
    link.rel = "stylesheet";
    document.head.appendChild(link);

    // Load countries first
    this.loadCountries();

    // Enable local UI-only development with ?mockPersonEdit or localStorage flag (dev builds only)
    const isDevBuild = process.env.NODE_ENV !== "production";
    const url = new URL(window.location.href);
    const hasMockQuery = url.searchParams.has("mockPersonEdit");
    const hasMockStorage =
      window.localStorage.getItem("mockPersonEdit") === "1";
    this.devBypass = isDevBuild && (hasMockQuery || hasMockStorage);

    if (this.devBypass) {
      this.initDevMock();
      return;
    }

    let jsonId = this.$route.query.key; // Adjust the key as per your routing setup
    if (jsonId) {
      if (jsonId === "TEST") {
        this.json_data = {
          firstName: "TEST",
          lastName: "TEST",
          institutionName: "TEST",
          primaryJobTitle: "TEST",
        };
      } else {
        try {
          // Load from the DB, not the S3-backed person view: the S3 JSON lags
          // behind saves, so the form showed (and could re-save) stale values.
          // (TAIR3-979)
          const { credentialId, secretKey } = this.getCredentials();
          let response = await Api().post("user/person-edit-details", {
            communityId: jsonId,
            credentialId,
            secretKey,
          });
          this.json_data = response.data;
          this.originalEmail = this.json_data.email || null;
          this.sendEmail = this.json_data.sendEmail === "T";
          this.displayEmail = this.json_data.displayEmail === "T";
          // Use the viewed person's ORCID from the API response, not the logged-in user's
          this.orcidFullId = this.json_data.orcidId || "";

          // Load states/provinces for the current country
          // Also normalize country/state values if they're abbreviations (e.g., "USA" -> "United States")
          if (this.json_data.country) {
            const selectedCountry = this.findCountryByValue(
              this.json_data.country
            );
            if (selectedCountry) {
              // Normalize country value to full name for dropdown matching
              this.json_data.country = selectedCountry.value;
              this.loadStatesForCountry(selectedCountry.isoCode);

              // Normalize state/region value if needed
              if (this.json_data.region) {
                const selectedState = this.findStateByValue(
                  this.json_data.region
                );
                if (selectedState) {
                  this.json_data.region = selectedState.value;
                }
              }
            }
          }

          this.loading = true;
          this.loading = false;
          document.title = `Edit Person`;
        } catch (error) {
          // Log only the message: the Axios error carries the request body,
          // which includes secretKey. (TAIR3-979)
          console.error("Error fetching Person details: ", error.message);
          this.loadingError = true;
        }
      }
    }
  },
  computed: {
    isFormValid() {
      // Safely check if the properties exist
      const password =
        this.json_data && this.json_data.password
          ? this.json_data.password
          : "";
      const passwordConfirm =
        this.json_data && this.json_data.password_confirm
          ? this.json_data.password_confirm
          : "";
      // Check if both password fields are empty
      const bothEmpty = !password && !passwordConfirm;
      // Check if the passwords are the same and within the required length
      const passwordsValid =
        password === passwordConfirm &&
        password.length >= 6 &&
        password.length <= 10;
      // The form is valid if both fields are empty or if the passwords are valid
      return bothEmpty || passwordsValid;
    },
    canUpdateLoginDetails() {
      // Only enable button when user has entered a valid new password
      const password =
        this.json_data && this.json_data.password
          ? this.json_data.password
          : "";
      const passwordConfirm =
        this.json_data && this.json_data.password_confirm
          ? this.json_data.password_confirm
          : "";
      // Password must be provided, valid length, and match confirmation
      return (
        password.length >= 6 &&
        password.length <= 10 &&
        password === passwordConfirm
      );
    },
    countryOptions() {
      return [{ value: "", text: "Select country/region" }, ...this.countries];
    },
    stateProvinceOptions() {
      return [
        { value: "", text: "Select a state/province" },
        ...this.statesProvinces,
      ];
    },
    // Check if current user is a TAIR curator (PWL-983)
    isTairCurator() {
      return this.getIsTairCurator() === true;
    },
    // Check if curator is viewing another user's profile
    isViewingOtherProfile() {
      const loggedInCommunityId = this.getCommunityId();
      return (
        this.json_data &&
        String(this.json_data.communityId) !== String(loggedInCommunityId)
      );
    },
  },
  watch: {
    "json_data.password"(newVal) {
      if (this.json_data) {
        this.validatePasswords();
      }
    },
    "json_data.password_confirm"(newVal) {
      if (this.json_data) {
        this.validatePasswords();
      }
    },
  },
  methods: {
    ...mapGetters({
      getCommunityId: "authProfile/getCommunityId",
      getCredentials: "authProfile/getCredentials",
      getOrcidId: "authProfile/getOrcidId",
      getPartyId: "authProfile/getPartyId",
      getIsTairCurator: "authProfile/getIsTairCurator",
    }),
    // Load countries from the library
    loadCountries() {
      const allCountries = Country.getAllCountries();
      this.countries = allCountries.map((country) => ({
        text: country.name,
        value: country.name,
        isoCode: country.isoCode,
      }));
    },

    // Find a country by name, ISO code, or common abbreviation (e.g., "USA" -> "US")
    findCountryByValue(value) {
      if (!value) return null;
      const normalizedValue = value.toUpperCase().trim();
      // Handle common variations like "USA" -> "US"
      const isoVariations = {
        USA: "US",
        UK: "GB",
      };
      const normalizedIso = isoVariations[normalizedValue] || normalizedValue;

      return this.countries.find(
        (c) =>
          c.value.toUpperCase() === normalizedValue ||
          c.isoCode.toUpperCase() === normalizedValue ||
          c.isoCode.toUpperCase() === normalizedIso
      );
    },

    // Find a state by name or ISO code (e.g., "NY" -> "New York")
    findStateByValue(value) {
      if (!value) return null;
      const normalizedValue = value.toUpperCase().trim();

      return this.statesProvinces.find(
        (s) =>
          s.value.toUpperCase() === normalizedValue ||
          (s.isoCode && s.isoCode.toUpperCase() === normalizedValue)
      );
    },

    // Load states/provinces based on selected country
    loadStatesForCountry(countryIsoCode) {
      if (!countryIsoCode) {
        this.statesProvinces = [];
        return;
      }

      const states = State.getStatesOfCountry(countryIsoCode);
      if (states && states.length > 0) {
        this.statesProvinces = states.map((state) => ({
          text: state.name,
          value: state.name,
          isoCode: state.isoCode,
        }));
      } else {
        this.statesProvinces = [];
      }
    },

    // Handle country selection change
    onCountryChange() {
      const selectedCountry = this.countries.find(
        (c) => c.value === this.json_data.country
      );
      if (selectedCountry) {
        this.loadStatesForCountry(selectedCountry.isoCode);
        // Reset state selection when country changes
        this.json_data.region = "";
      }
    },

    initDevMock() {
      // Simulate an authenticated user and fill UI with mock data for local development
      this.loading = false;
      this.loadingError = false;

      this.json_data = {
        communityId: "dev-community-123",
        userName: "devuser",
        email: "dev.user@example.com",
        password: "",
        password_confirm: "",
        firstName: "Dev",
        lastName: "User",
        institutionName: "Mock Institute of Botany",
        country: "United States",
        region: "California",
        sendEmail: "T",
        displayEmail: "F",
      };

      this.sendEmail = this.json_data.sendEmail === "T";
      this.displayEmail = this.json_data.displayEmail === "T";
      this.orcidFullId = "0000-0001-2345-6789";

      // Load states for the mock country
      const selectedCountry = this.countries.find(
        (c) => c.value === this.json_data.country
      );
      if (selectedCountry) {
        this.loadStatesForCountry(selectedCountry.isoCode);
      }

      document.title = "Edit Person (Dev Mode)";
    },
    validatePasswords() {
      // Directly access json_data properties with fallbacks to handle uninitialized states
      const password = this.json_data ? this.json_data.password || "" : "";
      const passwordConfirm = this.json_data
        ? this.json_data.password_confirm || ""
        : "";

      // Reset error messages
      this.passwordError = "";
      this.confirmPasswordError = "";

      // Validation logic remains unchanged
      if (
        password.length > 0 &&
        (password.length < 6 || password.length > 10)
      ) {
        this.passwordError = "Password must be between 6 and 10 characters.";
        return;
      }
      if (password !== passwordConfirm) {
        this.confirmPasswordError = "Passwords do not match.";
      }
    },

    async editAccount() {
      if (this.devBypass) {
        this.isLoading = true;
        this.accountFormErrors = [];
        setTimeout(() => {
          this.successMessage2 =
            "Person credentials updated successfully (dev mode - no actual update).";
          this.isLoading = false;
        }, 500);
        return;
      }

      this.isLoading = true;
      this.accountFormErrors = [];

      // Prepare account data
      if (!this.json_data.password) {
        this.json_data.password = "";
        this.json_data.password_confirm = "";
      }
      const { credentialId, secretKey } = this.getCredentials();
      const accountData = {
        credentialId: credentialId,
        secretKey: secretKey,
        communityId: this.json_data.communityId,
        userName: this.json_data.userName,
        password: this.json_data.password,
      };
      try {
        // Perform API call for account creation
        const response = await Api().put(
          "user/update-credentials",
          accountData
        );
        if (response.status === 200) {
          this.successMessage2 = "Person credentials updated successfully.";
          setTimeout(() => {
            this.$router.push({
              path: `/person`,
              query: { key: accountData.communityId.toString() },
            });
            this.successMessage = "";
          }, 2000); // 1000 milliseconds = 1 second
        } else {
          // Handle non-successful response
          throw new Error(`Failed to create account: ${response.status}`);
        }
      } catch (error) {
        this.isLoading = false;
        if (
          error.response &&
          error.response.status >= 400 &&
          error.response.status < 600
        ) {
          const message =
            error.response.data.error ||
            "An unexpected error occurred. Please try again.";
          this.accountFormErrors.push(message);
        } else {
          this.accountFormErrors.push(
            "Failed to update details due to a network or server issue. Please try again."
          );
        }
      }
    },
    async updatePersonDetails() {
      if (this.devBypass) {
        this.isLoading = true;
        this.updateFormErrors = [];
        setTimeout(() => {
          this.successMessage =
            "Person details updated successfully (dev mode - no actual update).";
          this.isLoading = false;
        }, 500);
        return;
      }

      this.isLoading = true;
      this.updateFormErrors = [];
      try {
        const emailChanged =
          this.json_data.email !== this.originalEmail;
        // The server authorizes every profile update against these. (TAIR3-979)
        const { credentialId, secretKey } = this.getCredentials();
        const updatedPersonDetails = {
          credentialId,
          secretKey,
          communityId: this.json_data.communityId,
          firstName: this.json_data.firstName,
          lastName: this.json_data.lastName,
          institutionName: this.json_data.institutionName,
          sendEmail: this.sendEmail ? "T" : "F",
          displayEmail: this.displayEmail ? "T" : "F",
          country: this.json_data.country,
          stateProvince: this.json_data.region,
        };
        if (emailChanged) {
          updatedPersonDetails.email = this.json_data.email;
        }

        const response = await Api().put(
          "user/update-person",
          updatedPersonDetails
        );
        this.isLoading = false;

        // Check for success response status (e.g., 200 OK)
        if (response.status === 200) {
          // Editing your own record returns to your account Profile page. A
          // curator editing someone else goes to that person's page instead -
          // /profile always shows the logged-in user. Both read the DB, so
          // the change shows immediately. The public page 404s for inactive
          // accounts, so a curator editing one stays here. (TAIR3-979)
          this.successMessage = "Person details updated successfully.";
          let destination = { path: "/profile" };
          if (this.isViewingOtherProfile) {
            destination =
              this.json_data.status === "active"
                ? {
                    path: "/person",
                    query: { key: String(updatedPersonDetails.communityId) },
                  }
                : null;
          }
          if (destination) {
            setTimeout(() => {
              this.$router.push(destination);
              this.successMessage = "";
            }, 2000);
          }
        } else {
          // Handle non-successful responses
          throw new Error(`Failed to update database: ${response.status}`);
        }
      } catch (error) {
        this.isLoading = false;
        // Log only the message: the Axios error carries the request body,
        // which includes secretKey. (TAIR3-979)
        console.error("Error updating person details:", error.message);
        // Surface the server's reason instead of a generic message so a failed
        // save can be diagnosed from what the user sees. (TAIR3-979)
        const serverError =
          error.response && error.response.data && error.response.data.error;
        this.updateFormErrors.push(
          serverError || "Failed to update details. Please try again."
        );
      }
    },

    // Helper method for password aria-describedby
    getPasswordAriaDescribedBy() {
      const describedBy = [];

      // Include help text when no errors are shown
      if (!this.passwordError) {
        describedBy.push("passwordHelp");
      }

      // Include error messages when they exist
      if (this.passwordError) {
        describedBy.push("passwordError");
      }

      return describedBy.length > 0 ? describedBy.join(" ") : null;
    },

    async handleUnlinkOrcid() {
      if (this.devBypass) {
        this.orcidFullId = "";
        alert("ORCID unlinked successfully (dev mode)");
        return;
      }
      try {
        this.isUnlinking = true;

        const targetName = this.json_data
          ? `${this.json_data.firstName} ${this.json_data.lastName}`
          : "this user";

        // Show confirmation dialog
        const confirmed = confirm(
          `Are you sure you want to unlink the ORCID account for ${targetName}?`
        );

        if (!confirmed) {
          this.isUnlinking = false;
          return;
        }

        const { credentialId, secretKey } = this.getCredentials();
        const targetCommunityId = this.json_data.communityId;

        const response = await unlinkOrcid(
          credentialId,
          secretKey,
          targetCommunityId
        );

        if (response.success) {
          this.orcidFullId = "";
          alert(
            `ORCID account successfully unlinked for ${targetName}.`
          );
        }
      } catch (error) {
        // Log only the message: the Axios error carries the request body,
        // which includes secretKey. (TAIR3-979)
        console.error("Error unlinking ORCID:", error.message);
        let errorMessage = "Failed to unlink ORCID account. Please try again.";
        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }
        alert(errorMessage);
      } finally {
        this.isUnlinking = false;
      }
    },

    // PWL-983: Handle account deactivation (curator only)
    async handleDeactivateAccount(bvModalEvent) {
      // Prevent modal from closing automatically
      bvModalEvent.preventDefault();

      if (this.devBypass) {
        this.showDeactivateModal = false;
        this.deactivateSuccess =
          "Account deactivated successfully (dev mode - no actual deactivation).";
        return;
      }

      this.isDeactivating = true;
      this.deactivateError = "";
      this.deactivateSuccess = "";

      try {
        const { credentialId, secretKey } = this.getCredentials();
        const targetCommunityId = this.json_data.communityId;

        const response = await Api().post("user/deactivate", {
          targetCommunityId: targetCommunityId,
          credentialId: credentialId,
          secretKey: secretKey,
        });

        if (response.status === 200 && response.data.success) {
          this.showDeactivateModal = false;
          this.deactivateSuccess = `Account for ${this.json_data.firstName} ${this.json_data.lastName} (${this.json_data.userName}) has been successfully deactivated.`;

          // Optionally redirect after a delay
          setTimeout(() => {
            this.$router.push({ path: "/profile" });
          }, 3000);
        } else {
          throw new Error(
            response.data.error || "Failed to deactivate account"
          );
        }
      } catch (error) {
        // Log only the message: the Axios error carries the request body,
        // which includes secretKey. (TAIR3-979)
        console.error("Error deactivating account:", error.message);
        let errorMessage = "Failed to deactivate account. Please try again.";
        
        // Check for partial failure (DB updated but PAM failed)
        if (error.response && error.response.data) {
          const responseData = error.response.data;
          if (responseData.partialSuccess && responseData.dbUpdated && !responseData.pamUpdated) {
            errorMessage = responseData.error || "Partial failure: The account was deactivated in the database but the authentication system update failed. Please contact an administrator to complete the deactivation.";
          } else if (responseData.error) {
            errorMessage = responseData.error;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
        this.deactivateError = errorMessage;
        this.showDeactivateModal = false;
      } finally {
        this.isDeactivating = false;
      }
    },
  },
};
</script>

<style scoped>
.card-title {
  color: #0c335d;
  margin-bottom: 1rem;
}

.edit-person-profile {
  background-color: #f8f9fa;
  padding: 2rem 0;
}

@media (max-width: 768px) {
  .edit-person-profile {
    padding: 1rem 0;
  }
}

/* PWL-983: Deactivation card styling */
.deactivate-card {
  border-color: #dc3545;
  border-width: 2px;
}

.deactivate-card .card-title {
  color: #dc3545;
}
</style>
